import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@common/components/ui/form"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@common/components/ui/select"
import { cn } from "@common/lib/utils"
import { type ComponentPropsWithoutRef, type ReactNode, useMemo } from "react"
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form"
import { useFormContext } from "react-hook-form"
import { FieldLabel } from "./label"
import { FieldRow } from "./row"

const NULL_SENTINEL = "__null__"

export type Option<V> = {
  value: V
  label: string
  disabled?: boolean
}

export type OptionGroup<V> = {
  label: string
  options: ReadonlyArray<Option<V>>
}

type BareBaseProps<T extends FieldValues, V> = Omit<
  ComponentPropsWithoutRef<typeof SelectTrigger>,
  "name" | "value" | "defaultValue" | "onChange" | "children"
> & {
  name: Path<T>
  placeholder?: string
  optional?: boolean
  clearable?: boolean
  disabled?: boolean
  icon?: ReactNode
  emptyLabel?: string
  onContentClick?: (event: React.MouseEvent) => void
  onPick?: (value: V | null) => void
}

function selected<V>(
  next: string,
  emptiable: boolean,
  lookup: Map<string, V>,
): V | null {
  return emptiable && next === NULL_SENTINEL
    ? null
    : ((lookup.get(next) ?? next) as V)
}

type BareProps<T extends FieldValues, V> = BareBaseProps<T, V> &
  (
    | { options: ReadonlyArray<Option<V>>; groups?: never }
    | { groups: ReadonlyArray<OptionGroup<V>>; options?: never }
  )

export function BareSelectField<T extends FieldValues, V = string>({
  name,
  ...rest
}: BareProps<T, V>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <SelectInput<T, V>
          field={field}
          invalid={!!fieldState.error}
          withFormControl={false}
          {...rest}
        />
      )}
    />
  )
}

function SelectInput<T extends FieldValues, V>({
  field,
  invalid,
  withFormControl,
  placeholder,
  optional,
  clearable,
  disabled,
  icon,
  emptyLabel,
  onContentClick,
  onPick,
  options,
  groups,
  ...rest
}: Omit<BareProps<T, V>, "name"> & {
  field: ControllerRenderProps<T, Path<T>>
  invalid: boolean
  withFormControl: boolean
}) {
  const emptiable = Boolean(optional || clearable)
  const lookup = useMemo(() => {
    const all = options ?? groups?.flatMap((g) => g.options) ?? []
    return new Map(all.map((opt) => [String(opt.value), opt.value]))
  }, [options, groups])

  const trigger = (
    <SelectTrigger
      aria-required={!optional}
      aria-invalid={invalid ? true : undefined}
      {...rest}
    >
      {icon}
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  )

  return (
    <Select
      value={
        field.value == null
          ? emptiable
            ? NULL_SENTINEL
            : ""
          : String(field.value)
      }
      onValueChange={(next) => {
        if (next === "") return
        const value = selected(next, emptiable, lookup)
        field.onChange(value)
        onPick?.(value)
      }}
      disabled={disabled}
    >
      {withFormControl ? <FormControl>{trigger}</FormControl> : trigger}
      <SelectContent onClick={onContentClick}>
        {emptiable && (
          <SelectItem value={NULL_SENTINEL}>{emptyLabel ?? "None"}</SelectItem>
        )}
        {options
          ? options.map((opt) => (
              <SelectItem
                key={String(opt.value)}
                value={String(opt.value)}
                disabled={opt.disabled}
              >
                {opt.label}
              </SelectItem>
            ))
          : groups?.map((group) => (
              <SelectGroup key={group.label}>
                <SelectLabel>{group.label}</SelectLabel>
                {group.options.map((opt) => (
                  <SelectItem
                    key={String(opt.value)}
                    value={String(opt.value)}
                    disabled={opt.disabled}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
      </SelectContent>
    </Select>
  )
}

type Props<T extends FieldValues, V> = BareProps<T, V> & { label: string }

export function SelectField<T extends FieldValues, V = string>({
  name,
  label,
  className,
  optional,
  ...rest
}: Omit<Props<T, V>, "label"> & { label?: string }) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          {label !== undefined && (
            <FieldLabel label={label} optional={optional} />
          )}
          <SelectInput<T, V>
            field={field}
            invalid={!!fieldState.error}
            withFormControl
            optional={optional}
            {...rest}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function RowSelectField<T extends FieldValues, V = string>({
  name,
  label,
  className,
  optional,
  ...rest
}: Props<T, V>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={cn("space-y-0", className)}>
          <FieldRow
            label={
              <FieldLabel
                label={label}
                optional={optional}
                className="text-body-medium"
              />
            }
          >
            <SelectInput<T, V>
              field={field}
              invalid={!!fieldState.error}
              withFormControl
              optional={optional}
              {...rest}
            />
            <FormMessage />
          </FieldRow>
        </FormItem>
      )}
    />
  )
}
